// JobInvoice.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios'; // Import axios
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Grid,
  CircularProgress, // Added
  Alert, // Added
  IconButton,
  TextField,
  Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useUser } from '../context/UserContext';
import { useOrg } from '../context/OrgContext';
import EditInvoiceDialog from './EditInvoiceDialog';
import { canEditInvoiceFully, canAddInvoiceFees, canSeeCustomerCharge } from '../utils/invoicePermissions';

const JobInvoice = ({ job }) => {
  const { name: orgName } = useOrg();
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editInvoice, setEditInvoice] = useState({
    open: false,
    jobId: null
  });
  const [invoiceNotes, setInvoiceNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState(null);
  const { currentUser: user } = useUser();

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Fetch invoice details from the backend
  useEffect(() => {
    if (!job || !job.id) {
      setInvoiceDetails(null); // Clear details if no job ID
      return;
    }

    const fetchInvoiceDetails = async () => {
      setLoading(true);
      setError(null);
      setInvoiceDetails(null); // Clear previous details
      
      // Always fetch from the API to ensure we have the latest calculated/saved invoice
      
      // If no saved invoice data, fetch from the API
      try {
        // console.log('Fetching invoice details from API for job:', job.id);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${job.id}/invoice-details`, {
          headers: getAuthHeader()
        });
        setInvoiceDetails(response.data);
        setInvoiceNotes(response.data.invoiceNoteHistory || job.invoiceNoteHistory || []);
      } catch (err) {
        console.error(`Error fetching invoice details for job ${job.id}:`, err);
        setError(err.response?.data?.message || err.message || 'Failed to load invoice details.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [job, getAuthHeader]); // Re-fetch if job object changes

  // Append an attributed note explaining an invoice change
  const handleAddNote = async () => {
    const text = newNote.trim();
    if (!text || !job?.id) return;
    setNoteSaving(true);
    setNoteError(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/jobs/${job.id}/invoice-notes`,
        { text },
        { headers: getAuthHeader() }
      );
      setInvoiceNotes(response.data.invoiceNoteHistory || []);
      setNewNote('');
    } catch (err) {
      console.error(`Error adding invoice note for job ${job.id}:`, err);
      setNoteError(err.response?.data?.message || 'Failed to save note.');
    } finally {
      setNoteSaving(false);
    }
  };
  
  // Handler for opening the edit invoice dialog
  const handleEditInvoice = () => {
    if (!job || !job.id) return;
    
    setEditInvoice({
      open: true,
      jobId: job.id
    });
  };
  
  // Handler for closing the edit invoice dialog
  const handleCloseEditInvoice = () => {
    setEditInvoice({
      open: false,
      jobId: null
    });
  };
  
  // Handler for saving invoice changes
  const handleSaveInvoice = () => {
    // Refresh the invoice data after saving
    if (job && job.id) {
      // First fetch the updated job object to get the saved invoice data
      const fetchUpdatedJob = async () => {
        setLoading(true);
        try {
          console.log('Fetching updated job after invoice save:', job.id);
          const jobResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${job.id}`, {
            headers: getAuthHeader()
          });
          
          // Check if the job has saved invoice data
          if (jobResponse.data.invoice) {
            console.log('Found saved invoice data in updated job:', jobResponse.data.invoice);
            // Convert the saved invoice data to the format expected by the component
            const savedInvoiceData = {
              baseService: {
                description: 'Service Fee',
                rate: jobResponse.data.invoice.items.find(item => item.item === 'Service Fee')?.price || 0,
                amount: jobResponse.data.invoice.items.find(item => item.item === 'Service Fee')?.total || 0,
              },
              enRouteMileage: {
                description: 'En Route Mileage',
                miles: jobResponse.data.invoice.items.find(item => item.item === 'En Route Per Mile')?.qty || 0,
                rate: jobResponse.data.invoice.items.find(item => item.item === 'En Route Per Mile')?.price || 0,
                amount: jobResponse.data.invoice.items.find(item => item.item === 'En Route Per Mile')?.total || 0,
              },
              totalCost: jobResponse.data.invoice.total || 0
            };
            setInvoiceDetails(savedInvoiceData);
          } else {
            // If no saved invoice data, fall back to the API
            console.log('No saved invoice data found, fetching from API');
            const invoiceResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${job.id}/invoice-details`, {
              headers: getAuthHeader()
            });
            setInvoiceDetails(invoiceResponse.data);
          }
        } catch (err) {
          console.error(`Error refreshing job/invoice details:`, err);
          // If fetching the job fails, try the invoice-details endpoint as a fallback
          try {
            const invoiceResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${job.id}/invoice-details`, {
              headers: getAuthHeader()
            });
            setInvoiceDetails(invoiceResponse.data);
          } catch (invoiceErr) {
            console.error(`Error fetching invoice details fallback:`, invoiceErr);
          }
        } finally {
          setLoading(false);
        }
      };
      
      fetchUpdatedJob();
    }
  };
  
  // White Knight admins and up edit everything; the provider or driver on the job may only add fees
  const canEditInvoice = () => canEditInvoiceFully(user) || canAddInvoiceFees(job, user);

  // Function to render invoice items from fetched data
  const renderInvoiceItems = () => {
    if (!invoiceDetails) return [];
    // If the API returns an "invoice" object, use its items array directly
    if (invoiceDetails.invoice && Array.isArray(invoiceDetails.invoice.items)) {
      return invoiceDetails.invoice.items.map(item => ({
        description: item.item,
        quantity: item.qty,
        rate: item.price || 0,
        amount: item.total || 0,
        pending: item.approvalStatus === 'pending',
        addedByName: item.addedByName,
      }));
    }
    // If the API returns a flat items array (legacy), use it directly
    if (Array.isArray(invoiceDetails.items)) {
      return invoiceDetails.items.map(item => ({
        description: item.item,
        quantity: item.qty,
        rate: item.price || 0,
        amount: item.total || 0,
        pending: item.approvalStatus === 'pending',
        addedByName: item.addedByName,
      }));
    }
    // Fallback: try to build from baseService/enRouteMileage/loadedMileage if present
    const items = [];
    if (invoiceDetails.baseService) {
      items.push({
        description: invoiceDetails.baseService.description || `Service: ${job.service}`,
        quantity: 1,
        rate: invoiceDetails.baseService.rate || 0,
        amount: invoiceDetails.baseService.amount || 0,
      });
    }
    if (invoiceDetails.enRouteMileage) {
      items.push({
        description: invoiceDetails.enRouteMileage.description || 'En Route Mileage',
        quantity: invoiceDetails.enRouteMileage.miles?.toFixed(1) || 0,
        rate: invoiceDetails.enRouteMileage.rate || 0,
        amount: invoiceDetails.enRouteMileage.amount || 0,
      });
    }
    if (invoiceDetails.loadedMileage) {
      items.push({
        description: invoiceDetails.loadedMileage.description || 'Loaded Mileage',
        quantity: invoiceDetails.loadedMileage.miles?.toFixed(1) || 0,
        rate: invoiceDetails.loadedMileage.rate || 0,
        amount: invoiceDetails.loadedMileage.amount || 0,
      });
    }
    return items;
  };

  const invoiceItems = renderInvoiceItems();
  // Use the total from the API response if available, otherwise fallback to 0
  const total = (invoiceDetails?.invoice?.total ?? invoiceDetails?.total ?? invoiceDetails?.totalCost ?? 0);

  if (!job) return null;

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const savedInvoice = invoiceDetails?.invoice || job.invoice || null;
  const mileageNote = savedInvoice?.distanceSource === 'road'
    ? 'Mileage is routed driving distance.'
    : savedInvoice?.distanceSource === 'estimate'
      ? 'Mileage is a straight-line estimate scaled for roads (routing service unavailable).'
      : null;

  // What the customer was charged in the roadside app — White Knight admins and up only
  const charge = canSeeCustomerCharge(user) ? job.customerCharge || null : null;
  const breakdown = charge?.breakdown || null;
  const customerRows = breakdown ? [
    { label: 'Base price', qty: 1, rate: breakdown.basePrice, amount: breakdown.basePrice },
    ...(breakdown.towMiles > 0 ? [{
      label: `Tow mileage (${breakdown.towMiles} mi driven, ${breakdown.includedMiles} included)`,
      qty: breakdown.billableMiles,
      rate: breakdown.perMileRate,
      amount: breakdown.mileageCharge
    }] : []),
    ...(breakdown.discountAmount > 0 ? [{ label: `Member discount (${breakdown.discountPercent}%)`, qty: 1, rate: null, amount: -breakdown.discountAmount }] : []),
    ...(breakdown.tax > 0 ? [{ label: 'Tax', qty: 1, rate: null, amount: breakdown.tax }] : [])
  ] : [];
  const stripeLabel = charge?.paymentMethodType === 'cashapp' ? 'Cash App Pay (authorized in app)' : 'Card (authorized in app)';
  const paypalLabel = charge?.paymentMethodType === 'venmo' ? 'Venmo (authorized)' : 'PayPal (authorized)';
  const paymentLabel = { card: stripeLabel, paypal: paypalLabel, cash: 'Cash to driver', covered: 'Covered by membership' }[charge?.paymentMethod] || charge?.paymentMethod || '';

  return (
    <Box margin={2} sx={{ boxShadow: 1, borderRadius: 1, overflow: 'hidden' }}>
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Job Invoice
          </Typography>
          <Typography variant="subtitle1">
            Invoice #: {job.po || job.id}
          </Typography>
        </Box>
        
        {/* Job Basic Details */}
        <Box mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
              <Typography variant="body1">{job.customerName}</Typography>
              <Typography variant="body2">{job.customerPhone}</Typography>
              {job.customerEmail && <Typography variant="body2">{job.customerEmail}</Typography>}
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography variant="subtitle2" color="text.secondary">Service Address</Typography>
              <Typography variant="body1">
                {job.serviceLocation && typeof job.serviceLocation === 'object' 
                  ? `${job.serviceLocation.street}, ${job.serviceLocation.city}, ${job.serviceLocation.state} ${job.serviceLocation.zip}` 
                  : (job.serviceLocation || job.location)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Invoice Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">Quantity/Miles</TableCell> 
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={24} /> Loading Invoice...
                  </TableCell>
                </TableRow>
              ) : error ? (
                 <TableRow>
                  <TableCell colSpan={4}>
                    <Alert severity="error">{error}</Alert>
                  </TableCell>
                </TableRow>
              ) : invoiceItems.length > 0 ? (
                invoiceItems.map((item, index) => (
                  <TableRow key={index} sx={item.pending ? { backgroundColor: 'warning.light' } : undefined}>
                    <TableCell>
                      {item.description}
                      {item.pending && <Typography variant="caption" color="warning.dark" display="block">Pending {orgName} approval (not in total)</Typography>}
                      {!item.pending && item.addedByName && <Typography variant="caption" color="text.secondary" display="block">Added by {item.addedByName}</Typography>}
                    </TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">
                      {typeof item.rate === 'string' ? item.rate : `$${parseFloat(item.rate).toFixed(2)}`}
                    </TableCell>
                    <TableCell align="right">${parseFloat(item.amount).toFixed(2)}</TableCell>
                  </TableRow>
                ))
              ) : (
                 <TableRow>
                  <TableCell colSpan={4} align="center">
                    No invoice items calculated. Rates may be missing or calculation failed.
                  </TableCell>
                </TableRow>
              )}
              
              {/* Total Row - Only show if not loading and no error */}
              {!loading && !error && (
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    ${total.toFixed(2)}
                    {canEditInvoice() && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={handleEditInvoice}
                        sx={{ ml: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {/* Correctly placed closing tags */}
          </Table>
        </TableContainer>
        {mileageNote && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {mileageNote}
            {savedInvoice?.enRouteMiles != null ? ` En route ${savedInvoice.enRouteMiles} mi` : ''}
            {savedInvoice?.loadedMiles ? `, loaded ${savedInvoice.loadedMiles} mi.` : '.'}
          </Typography>
        )}

        {breakdown && (
          <Box mt={3}>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">Customer Price (roadside app)</Typography>
              <Typography variant="body2" color="text.secondary">
                {paymentLabel}{charge?.simulated ? ' · DEMO (nothing was charged)' : ''}{breakdown.pricingChoice === 'full' ? ' · full price chosen' : ''}
              </Typography>
            </Box>
            {breakdown.coveredNote && (
              <Typography variant="body2" color="text.secondary" gutterBottom>{breakdown.coveredNote}</Typography>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Quantity/Miles</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerRows.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">{row.qty}</TableCell>
                      <TableCell align="right">{row.rate != null ? money(row.rate) : '—'}</TableCell>
                      <TableCell align="right">{money(row.amount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Customer total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{money(breakdown.total)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {breakdown.distanceSource === 'estimate' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Tow miles were estimated from straight-line distance because the routing service did not answer.
              </Typography>
            )}
          </Box>
        )}

        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>Invoice Notes:</Typography>
          {(invoiceDetails?.invoice?.invoiceNotes || invoiceDetails?.invoiceNotes || job?.invoice?.invoiceNotes) && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} gutterBottom>
              {invoiceDetails?.invoice?.invoiceNotes || invoiceDetails?.invoiceNotes || job?.invoice?.invoiceNotes}
            </Typography>
          )}

          {/* Attributed note history: who noted what, and when */}
          {invoiceNotes.length > 0 ? (
            invoiceNotes.map((note, index) => (
              <Box key={note._id || index} mb={1} p={1} bgcolor="action.hover" borderRadius={1}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{note.text}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {note.addedByName || 'Unknown'}
                  {note.timestamp ? ` — ${new Date(note.timestamp).toLocaleString()}` : ''}
                </Typography>
              </Box>
            ))
          ) : (
            !(invoiceDetails?.invoice?.invoiceNotes || invoiceDetails?.invoiceNotes || job?.invoice?.invoiceNotes) && (
              <Typography variant="body2" color="text.secondary">
                No invoice notes yet.
              </Typography>
            )
          )}

          {canEditInvoice() && (
            <Box mt={1} display="flex" gap={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={4}
                placeholder="Add a note (e.g. why a line item was added or changed)"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                disabled={noteSaving}
                inputProps={{ maxLength: 2000 }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleAddNote}
                disabled={noteSaving || !newNote.trim()}
                sx={{ whiteSpace: 'nowrap', mt: '2px' }}
              >
                {noteSaving ? 'Saving…' : 'Add Note'}
              </Button>
            </Box>
          )}
          {noteError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {noteError}
            </Typography>
          )}
        </Box>
        
        {/* Payment Status */}
        <Box mt={2} p={1} bgcolor={job.paymentSubmitted ? 'success.light' : 'warning.light'} borderRadius={1}>
          <Typography variant="body2" color="text.secondary">
            Payment Status: {job.paymentSubmitted ? 'Submitted' : 'Pending'}
          </Typography>
        </Box>
      </Paper>
      {/* Edit Invoice Dialog */}
      <EditInvoiceDialog
        open={editInvoice.open}
        onClose={handleCloseEditInvoice}
        jobId={editInvoice.jobId}
        onSave={handleSaveInvoice}
      />
    </Box>
  );
};

export default JobInvoice;
